import { useState } from "react";

import { useGetIdentity, useNotification, useTranslation } from "@refinedev/core";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";

import { PasswordEntryFields, usePasswordValidation } from "@/components/auth";
import { CardButton } from "@/components/common";
import { IUser } from "@/types";
import { CHANGE_PASSWORD_MUTATION, ChangePasswordData, ChangePasswordInput } from "@/utils/GraphQLClient";

import { useMutation } from "urql";

import { Breadcrumb } from "./Breadcrumb";

export const ChangePasswordPanel: React.FC = () => {
  const { translate } = useTranslation();
  const { data: user } = useGetIdentity<IUser>();
  const { open: openNotification } = useNotification();

  // urql mutation hook
  const [changePasswordResult, changePasswordMutation] = useMutation<
    ChangePasswordData,
    { input: ChangePasswordInput }
  >(CHANGE_PASSWORD_MUTATION);

  // Form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  // UI state
  const [error, setError] = useState<string | null>(null);

  // Validation
  const { passwordsMatch, allRequirementsMet } = usePasswordValidation(newPassword, confirmPassword);
  const isFormValid =
    currentPassword.trim() !== "" &&
    newPassword.length > 0 &&
    allRequirementsMet &&
    passwordsMatch &&
    confirmPassword.length > 0;

  const handleSubmit = async () => {
    // Check user identity first
    if (!user?.email) {
      setError(translate("pages.profile.changePassword.userNotLoaded"));
      return;
    }

    // Then check form validation
    if (!isFormValid) {
      setError(translate("pages.profile.changePassword.validationError"));
      return;
    }

    setError(null);

    try {
      const result = await changePasswordMutation({
        input: {
          email: user.email,
          oldPassword: currentPassword.trim(),
          newPassword: newPassword.trim(),
        },
      });

      if (result.error) {
        const errorMessage = result.error.graphQLErrors?.[0]?.message ?? result.error.message;
        if (
          errorMessage?.toLowerCase().includes("incorrect") ||
          errorMessage?.toLowerCase().includes("invalid") ||
          errorMessage?.toLowerCase().includes("wrong password")
        ) {
          setError(translate("pages.profile.changePassword.incorrectPassword"));
        } else {
          setError(errorMessage ?? translate("pages.profile.changePassword.error"));
        }
        return;
      }

      // Success - mutation returns message only, no success boolean
      if (result.data?.changePassword?.message) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        openNotification?.({
          type: "success",
          message: translate("pages.profile.changePassword.success"),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : translate("pages.profile.changePassword.error"));
    }
  };

  return (
    <Box>
      <Breadcrumb
        items={[
          { label: translate("pages.profile.sections.profileAndSecurity"), to: "/profile?section=profile-security" },
          { label: translate("pages.profile.options.changePassword") },
        ]}
      />

      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            {/* Current Password Field */}
            <FormControl fullWidth variant="outlined">
              <InputLabel shrink required>
                {translate("pages.profile.changePassword.currentPassword")}
              </InputLabel>
              <OutlinedInput
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={e => {
                  setCurrentPassword(e.target.value);
                  setError(null);
                }}
                disabled={changePasswordResult.fetching}
                inputProps={{ "data-testid": "current-password-input" }}
                placeholder="••••••••••"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showCurrentPassword ? "hide the password" : "display the password"}
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      onMouseDown={e => e.preventDefault()}
                      onMouseUp={e => e.preventDefault()}
                      edge="end"
                      sx={{ color: "text.secondary" }}
                    >
                      {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            {/* New and Confirm Password Fields with Requirements */}
            <PasswordEntryFields
              newPassword={newPassword}
              confirmPassword={confirmPassword}
              onNewPasswordChange={value => {
                setNewPassword(value);
                setError(null);
              }}
              onConfirmPasswordChange={value => {
                setConfirmPassword(value);
                setError(null);
              }}
            />

            {/* Error message */}
            {error && (
              <Typography color="error.dark" variant="body2" data-testid="error-message">
                {error}
              </Typography>
            )}

            {/* Submit button */}
            <CardButton
              fullWidth
              height={52}
              onClick={handleSubmit}
              disabled={changePasswordResult.fetching || !isFormValid}
              data-testid="submit-button"
            >
              {translate("buttons.save")}
            </CardButton>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
